import React, { useMemo, useState } from 'react';
import { Calendar, Clock, ChevronRight, Search, X, ChevronLeft, ChevronDown, RefreshCw } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';

const BlogSection = ({
  isLoading,
  posts,
  currentBlogPosts,
  selectedFilter,
  setSelectedFilter,
  searchQuery,
  setSearchQuery,
  currentPage,
  setCurrentPage,
  totalPages,
  handleNavigate,
  setTextSizeMultiplier,
  sortOrder,
  setSortOrder,
  selectedYear,
  setSelectedYear,
  theme,
  isDarkMode
}) => {
  
  // The Zen Hammer for guarantee
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '32px' };
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);

  // Dynamically extract available Categories and Years based on active filters
  const { availableCategories, availableYears, categoryCounts, yearCounts } = useMemo(() => {
    const catCounts = {};
    const yrCounts = {};
    let totalCats = 0;
    let totalYrs = 0;
    
    posts.forEach(post => {
      if (post.status !== 'published') return;

      const postCat = post.category || 'Thought';
      const postYear = post.date ? new Date(post.date).getFullYear().toString() : '';

      // Check if the post matches the *other* active filters
      const matchCat = selectedFilter === 'All' || postCat === selectedFilter;
      const matchYear = selectedYear === 'All' || postYear === selectedYear;

      // Only add to available lists if the other filters aren't blocking this post
      if (matchYear && postCat) {
        catCounts[postCat] = (catCounts[postCat] || 0) + 1;
        totalCats++;
      }
      if (matchCat && postYear) {
        yrCounts[postYear] = (yrCounts[postYear] || 0) + 1;
        totalYrs++;
      }
    });

    catCounts['All'] = totalCats;
    yrCounts['All'] = totalYrs;

    return {
      availableCategories: Object.keys(catCounts).filter(k => k !== 'All').sort(),
      availableYears: Object.keys(yrCounts).filter(k => k !== 'All').sort((a, b) => b - a),
      categoryCounts: catCounts,
      yearCounts: yrCounts
    };
  }, [posts, selectedFilter, selectedYear]);

  // Ensure currently selected items stay visible in dropdowns so they can be unselected if they become invalid
  const displayCategories = selectedFilter !== 'All' && !availableCategories.includes(selectedFilter) ? [selectedFilter, ...availableCategories] : availableCategories;
  const displayYears = selectedYear !== 'All' && !availableYears.includes(selectedYear) ? [selectedYear, ...availableYears] : availableYears;

  const hasActiveFilters = searchQuery !== '' || sortOrder !== 'newest' || selectedFilter !== 'All' || selectedYear !== 'All';

  const handleResetFilters = () => {
    setSearchQuery('');
    setSortOrder('newest');
    setSelectedFilter('All');
    setSelectedYear('All');
    setCurrentPage(1);
  };

  return (
    <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="max-w-4xl mx-auto w-full">
        
        <style>{`
          @keyframes staggerFadeUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .stagger-item {
            animation: staggerFadeUp 0.6s ease-out both;
          }
        `}</style>
        
        {/* Header Section with Search */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <h2 className={`text-5xl md:text-6xl font-garamond mb-4 ${theme.text}`}>Reflections & Essays</h2>
            <p className={`text-sm tracking-wide opacity-60 ${theme.textMuted}`}>Deep dives into thoughts, philosophy, and personal growth.</p>
          </div>

          {/* Search Pill Input - Repositioned */}
          <div 
            style={pillStyle}
            className={`relative flex items-center w-full md:w-64 px-6 py-3 border transition-all duration-500 ${
              isDarkMode 
                ? 'bg-stone-900/40 border-stone-800 focus-within:border-stone-600' 
                : 'bg-stone-50/50 border-stone-100 focus-within:border-stone-300'
            }`}
          >
            <Search size={14} className="opacity-40" />
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchQuery} 
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} 
              className="ml-3 w-full bg-transparent text-xs font-medium outline-none placeholder:opacity-40" 
            />
            {searchQuery && (
              <button 
                onClick={() => { setSearchQuery(''); setCurrentPage(1); }} 
                className="ml-2 opacity-40 hover:opacity-100"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        
        {!isLoading && posts.length > 0 && (
          <div className="mb-16">
            {/* Single Line Filter Container - Horizontal Scroll */}
            <div className="flex items-center gap-3 overflow-x-auto pb-4 no-scrollbar">
                {/* Sort Button */}
                <button 
                  onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
                  style={pillStyle}
                  className={`flex-shrink-0 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap ${
                    isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400'
                  }`}
                >
                  {sortOrder === 'newest' ? 'Sort: Newest' : 'Sort: Oldest'}
                </button>

                <div className={`w-px h-5 flex-shrink-0 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

                {/* Category Dropdown */}
                <div className="relative flex-shrink-0">
                  <button
                      onClick={() => setIsCategoryOpen(prev => !prev)}
                      style={pillStyle}
                      className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                          selectedFilter !== 'All'
                              ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                              : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                      }`}
                  >
                      <span>{selectedFilter === 'All' ? 'Category' : selectedFilter}</span>
                      <ChevronDown size={14} className={`${isCategoryOpen ? 'rotate-180' : ''} transition-transform`} />
                  </button>

                  {isCategoryOpen && (
                      <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                          <div
                            className="absolute inset-0 bg-black/20 backdrop-blur-md" 
                            onClick={() => setIsCategoryOpen(false)}
                          />
                          <div
                              style={cardStyle}
                              className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}
                          >
                              <div className="flex flex-col gap-2">
                              <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Category</p>
                              {['All', ...displayCategories].map(cat => (
                                  <button
                                      key={cat}
                                      onClick={() => {
                                          setSelectedFilter(cat);
                                          setCurrentPage(1);
                                          setIsCategoryOpen(false);
                                      }}
                                      style={pillStyle}
                                      className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${
                                          selectedFilter === cat
                                              ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white')
                                              : `opacity-60 hover:opacity-100 ${theme.text}`
                                      }`}
                                  >
                                  <span>{cat === 'All' ? 'All Categories' : cat}</span>
                                  <span className="opacity-40 text-[10px]">{categoryCounts[cat] || 0}</span>
                                  </button>
                              ))}
                              </div>
                          </div>
                      </div>
                  )}
                </div>

                {/* Year Filter Pills */}
                {displayYears.length > 0 && (
                  <>
                    <div className={`w-px h-5 flex-shrink-0 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />
                    
                    {/* Year Dropdown */}
                    <div className="relative flex-shrink-0">
                      <button
                          onClick={() => setIsYearOpen(prev => !prev)}
                          style={pillStyle}
                          className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                              selectedYear !== 'All'
                                  ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                                  : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                          }`}
                      >
                          <span>{selectedYear === 'All' ? 'Year' : selectedYear}</span>
                          <ChevronDown size={14} className={`${isYearOpen ? 'rotate-180' : ''} transition-transform`} />
                      </button>

                      {isYearOpen && (
                          <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                              <div
                                className="absolute inset-0 bg-black/20 backdrop-blur-md" 
                                onClick={() => setIsYearOpen(false)}
                              />
                              <div
                                  style={cardStyle}
                                  className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}
                              >
                                  <div className="flex flex-col gap-2">
                                  <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Year</p>
                                  {['All', ...displayYears].map(year => (
                                      <button
                                          key={year}
                                          onClick={() => {
                                              setSelectedYear(year);
                                              setCurrentPage(1);
                                              setIsYearOpen(false);
                                          }}
                                          style={pillStyle}
                                          className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${
                                              selectedYear === year
                                                  ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white')
                                                  : `opacity-60 hover:opacity-100 ${theme.text}`
                                          }`}
                                      >
                                          <span>{year === 'All' ? 'All Time' : year}</span>
                                      <span className="opacity-40 text-[10px]">{yearCounts[year] || 0}</span>
                                      </button>
                                  ))}
                                  </div>
                              </div>
                          </div>
                      )}
                    </div>
                  </>
                )}

        {/* Reset Filters */}
        {hasActiveFilters && (
          <button
            onClick={handleResetFilters}
            style={pillStyle}
            className={`flex-shrink-0 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
              isDarkMode ? 'border-red-900/30 text-red-400 hover:bg-red-900/20' : 'border-red-100 text-red-500 hover:bg-red-50'
            }`}
          >
            <RefreshCw size={14} /> <span>Reset</span>
          </button>
        )}
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSpinner theme={theme} />
        ) : currentBlogPosts.length === 0 ? (
          <div className="text-center py-32 rounded-[40px] border border-dashed border-stone-200 dark:border-stone-800 opacity-50">
            <p className={`font-garamond italic text-xl ${theme.text}`}>No reflections found in this silent space.</p>
          </div>
        ) : (
          <div key={`blog-page-${currentPage}`} className="space-y-8">
            {currentBlogPosts.map((post, index) => (
              <article 
                key={post.id} 
                style={{ 
                  animationDelay: `${index * 100}ms`
                }}
                className={`stagger-item group cursor-pointer rounded-[40px] p-8 md:p-10 border transition-colors duration-700 ${
                  isDarkMode 
                    ? 'border-transparent hover:border-stone-800 hover:bg-stone-900/30' 
                    : 'border-transparent hover:border-stone-100 hover:bg-stone-50/50'
                }`} 
                onClick={() => { handleNavigate('post', post); setTextSizeMultiplier(1); }}
              >
                <div className="flex flex-col space-y-6">
                  <div className="flex items-center flex-wrap gap-4">
                    {/* The Category Pill Tag */}
                    <span 
                      style={pillStyle}
                      className={`px-4 py-1.5 text-[9px] font-bold uppercase tracking-widest border transition-colors ${
                        isDarkMode ? 'bg-transparent border-stone-800 text-stone-500' : 'bg-transparent border-stone-100 text-stone-400'
                      }`}>
                      {post.category || 'Thought'}
                    </span>
                    
                    <div className={`flex items-center space-x-6 text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>
                      <span className="flex items-center">
                        <Calendar size={12} className="mr-2" /> {post.date}
                      </span>
                      <span className="flex items-center">
                        <Clock size={12} className="mr-2" /> {post.readTime}
                      </span>
                    </div>

                    {post.status === 'draft' && (
                      <span style={pillStyle} className="px-3 py-1 border border-amber-500/30 text-amber-500 text-[9px] uppercase tracking-widest font-bold">
                        Draft
                      </span>
                    )}
                  </div>

                  <div className="space-y-4">
                    <h3 className={`text-4xl md:text-[40px] font-garamond leading-tight transition-all duration-500 ${theme.text} group-hover:translate-x-1`}>
                      {post.title}
                    </h3>
                    <p className={`text-lg md:text-xl leading-relaxed line-clamp-2 font-garamond opacity-50 group-hover:opacity-80 transition-opacity duration-700 ${theme.text}`}>
                      {post.excerpt}
                    </p>
                  </div>
                </div>
              </article>
            ))}
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-20 pt-10 border-t border-stone-100 dark:border-stone-900">
                <button 
                  onClick={() => {
                    setCurrentPage(currentPage - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} 
                  disabled={currentPage === 1} 
                  style={pillStyle}
                  className={`flex items-center space-x-3 px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                    currentPage === 1 
                      ? 'opacity-10 cursor-not-allowed border-transparent' 
                      : `border-transparent hover:border-stone-200 dark:hover:border-stone-800 ${theme.text}`
                  }`}
                >
                  <ChevronLeft size={16} /> <span>Prev</span>
                </button>
                
                <span className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 ${theme.text}`}>
                  {currentPage} / {totalPages}
                </span>
                
                <button 
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} 
                  disabled={currentPage === totalPages} 
                  style={pillStyle}
                  className={`flex items-center space-x-3 px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                    currentPage === totalPages 
                      ? 'opacity-10 cursor-not-allowed border-transparent' 
                      : `border-transparent hover:border-stone-200 dark:hover:border-stone-800 ${theme.text}`
                  }`}
                >
                  <span>Next</span> <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;